/**
 * Themed Quizzes — Pop-culture wrappers around shared behavioral questions.
 * Every question measures real traits; the theme only affects result labels.
 */

import { Sword, Sparkles, Shield, BookOpen, Rocket, Skull, Crown, Wand2, Swords, Building, Flame } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { GuestTest, GuestTestQuestion, ScaleDefinition, ResultTier } from './guestTests';
import { getQuestionsByDimensions, type PersonalityDimension, type SharedQuestion } from './sharedQuestionPool';
import type { MediaType } from '@/config/likerCategories';

interface ThemedArchetype {
  label: string;
  description: string;
  emoji: string;
  /** Dimension requirements: { dimension: 'high' | 'low' | 'any' } */
  profile: Partial<Record<string, 'high' | 'low' | 'any'>>;
  /** Detailed strengths associated with this archetype */
  strengths: string[];
  /** Growth areas for this archetype */
  growthAreas: string[];
  /** Reflective insight for the user */
  insight: string;
}

export interface ImplicitMediaInterest {
  category: string;
  title: string;
  confidence: number;
}

interface ThemedQuizDefinition {
  slug: string;
  name: string;
  description: string;
  icon: LucideIcon;
  emoji: string;
  color: string;
  estimatedTime: string;
  dimensions: PersonalityDimension[];
  questionsPerDimension: number;
  archetypes: ThemedArchetype[];
  implicitMediaInterests: ImplicitMediaInterest[];
}

const sharedTiers: ResultTier[] = [
  { minScore: 80, label: 'Very High', description: 'Exceptionally strong in this area', emoji: '🔥' },
  { minScore: 60, label: 'High', description: 'Above average presence', emoji: '⬆️' },
  { minScore: 40, label: 'Moderate', description: 'Balanced expression', emoji: '⚖️' },
  { minScore: 20, label: 'Low', description: 'Minimal presence', emoji: '⬇️' },
  { minScore: 0, label: 'Very Low', description: 'Rarely expressed', emoji: '🌊' },
];

function tierForScore(score: number): ResultTier {
  const sorted = [...sharedTiers].sort((a, b) => b.minScore - a.minScore);
  return sorted.find(t => score >= t.minScore) || sorted[sorted.length - 1];
}

const THEMED_QUIZ_DEFINITIONS: ThemedQuizDefinition[] = [
  {
    slug: 'got-character',
    name: 'Game of Thrones Character',
    description: 'Which Westeros character matches your personality? (Real behavioral questions, not trivia!)',
    icon: Sword,
    emoji: '⚔️',
    color: 'hsl(220 40% 35%)',
    estimatedTime: '4 min',
    dimensions: ['extraversion', 'assertiveness', 'moral_reasoning', 'risk_tolerance'],
    questionsPerDimension: 5,
    archetypes: [
      {
        label: 'Tyrion Lannister',
        description: 'Witty, strategic, and morally nuanced — you navigate complex situations with intellect and charm. You see angles others miss and use humor as both shield and sword. People underestimate you at their own peril.',
        emoji: '🍷',
        profile: { assertiveness: 'high', moral_reasoning: 'high', extraversion: 'high' },
        strengths: ['Strategic thinking under pressure', 'Reading people accurately', 'Finding creative solutions to impossible problems'],
        growthAreas: ['Trusting others more deeply', 'Letting go of cynicism', 'Accepting vulnerability as strength'],
        insight: 'You tend to use wit and intellect as armor. Consider that some of your best connections come when you let the mask slip.',
      },
      {
        label: 'Jon Snow',
        description: 'Principled and brave — you do what\'s right even when it costs you everything. You carry a quiet strength that inspires loyalty in others. Your moral compass is your greatest asset, even when it leads you into difficult territory.',
        emoji: '🐺',
        profile: { moral_reasoning: 'high', risk_tolerance: 'high', assertiveness: 'low' },
        strengths: ['Unwavering moral conviction', 'Leading by example rather than words', 'Earning deep loyalty from others'],
        growthAreas: ['Speaking up for yourself sooner', 'Recognizing when duty becomes self-sacrifice', 'Balancing idealism with pragmatism'],
        insight: 'Your instinct is to shoulder burdens alone. Remember that asking for help isn\'t weakness — it\'s how leaders build lasting teams.',
      },
      {
        label: 'Daenerys Targaryen',
        description: 'Bold and visionary — you challenge the established order with fierce conviction. You don\'t just want change; you embody it. Your passion can move mountains, but it needs wisdom as its companion.',
        emoji: '🐉',
        profile: { assertiveness: 'high', risk_tolerance: 'high' },
        strengths: ['Inspiring others toward a shared vision', 'Courage to break unjust systems', 'Resilience through personal hardship'],
        growthAreas: ['Listening to dissenting voices', 'Distinguishing between justice and vengeance', 'Patience with incremental progress'],
        insight: 'Your fire is your greatest gift — and your greatest risk. The leaders who endure are those who learn when to burn and when to build.',
      },
      {
        label: 'Cersei Lannister',
        description: 'Strategic and determined — you play the long game and protect what\'s yours. You understand power dynamics intuitively and aren\'t afraid to make hard choices. Your pragmatism is both your armor and your blind spot.',
        emoji: '👑',
        profile: { assertiveness: 'high', moral_reasoning: 'low', extraversion: 'low' },
        strengths: ['Long-term strategic planning', 'Fierce protection of inner circle', 'Understanding power dynamics'],
        growthAreas: ['Expanding your circle of trust', 'Considering others\' perspectives genuinely', 'Finding security beyond control'],
        insight: 'You\'ve learned that the world rewards strength — but your deepest connections come when you show the person behind the strategy.',
      },
      {
        label: 'Arya Stark',
        description: 'Independent and resourceful — you forge your own path with quiet determination. You adapt to any situation and refuse to be defined by others\' expectations. Your independence is hard-won and fiercely guarded.',
        emoji: '🗡️',
        profile: { risk_tolerance: 'high', extraversion: 'low' },
        strengths: ['Remarkable adaptability', 'Self-reliance in any situation', 'Persistence against impossible odds'],
        growthAreas: ['Allowing others to know the real you', 'Building lasting connections', 'Finding identity beyond survival mode'],
        insight: 'You\'ve proven you can survive anything alone. The next frontier is discovering what you want to build — not just what you\'re running from.',
      },
      {
        label: 'Samwell Tarly',
        description: 'Thoughtful and loyal — your courage comes from knowledge and compassion. You may not seek the spotlight, but your contributions run deeper than most realize. The bravest thing you do is show up authentically.',
        emoji: '📚',
        profile: { assertiveness: 'low', moral_reasoning: 'high', risk_tolerance: 'low' },
        strengths: ['Deep knowledge and curiosity', 'Genuine loyalty and compassion', 'Courage to act despite fear'],
        growthAreas: ['Trusting your own capabilities more', 'Speaking up in high-pressure situations', 'Recognizing your own bravery'],
        insight: 'You often see yourself as the sidekick in your own story. But the people around you know the truth: you\'re the one who makes the real difference.',
      },
    ],
    implicitMediaInterests: [
      { category: 'tv', title: 'Game of Thrones', confidence: 0.8 },
      { category: 'book', title: 'A Song of Ice and Fire', confidence: 0.5 },
    ],
  },
  {
    slug: 'hogwarts-house',
    name: 'Hogwarts House',
    description: 'Your house, determined by who you actually are — not which one you wish you were.',
    icon: Sparkles,
    emoji: '🏰',
    color: 'hsl(270 50% 40%)',
    estimatedTime: '4 min',
    dimensions: ['agreeableness', 'conscientiousness', 'openness', 'risk_tolerance'],
    questionsPerDimension: 5,
    archetypes: [
      {
        label: 'Gryffindor',
        description: 'Brave, daring, and driven by strong convictions — you charge into the unknown when others hesitate. Your courage isn\'t the absence of fear; it\'s the decision that something matters more than fear. You inspire others to find their own bravery.',
        emoji: '🦁',
        profile: { risk_tolerance: 'high', agreeableness: 'high' },
        strengths: ['Courage in the face of adversity', 'Standing up for others', 'Taking action when it matters most'],
        growthAreas: ['Thinking before leaping', 'Recognizing when caution serves better than courage', 'Accepting that not every battle is yours to fight'],
        insight: 'Your instinct to act is powerful — but the bravest Gryffindors learn that sometimes the hardest thing is to wait, listen, and choose your moment.',
      },
      {
        label: 'Ravenclaw',
        description: 'Curious, creative, and endlessly learning — knowledge is your superpower. You see patterns where others see chaos, and your mind is always reaching for the next insight. You value understanding not for what it earns you, but for its own sake.',
        emoji: '🦅',
        profile: { openness: 'high', conscientiousness: 'high' },
        strengths: ['Deep analytical thinking', 'Creative problem-solving', 'Intellectual curiosity that never rests'],
        growthAreas: ['Moving from analysis to action', 'Connecting with people emotionally, not just intellectually', 'Accepting that some things can\'t be fully understood'],
        insight: 'Your mind is your greatest instrument — but wisdom also comes from experiences that can\'t be found in books. Let yourself be surprised.',
      },
      {
        label: 'Hufflepuff',
        description: 'Loyal, hardworking, and genuinely kind — you value fairness and friendship above all. You\'re the person everyone trusts but few truly appreciate. Your strength isn\'t flashy; it\'s the bedrock that holds everything together.',
        emoji: '🦡',
        profile: { agreeableness: 'high', conscientiousness: 'high' },
        strengths: ['Building deep, lasting relationships', 'Consistent effort and reliability', 'Creating inclusive, supportive environments'],
        growthAreas: ['Setting boundaries without guilt', 'Recognizing your own worth', 'Speaking up when you disagree'],
        insight: 'You give so much to others that you sometimes forget to fill your own cup. Your kindness is not weakness — but it needs boundaries to be sustainable.',
      },
      {
        label: 'Slytherin',
        description: 'Ambitious, resourceful, and strategic — you know what you want and how to get it. You understand that the world doesn\'t hand out rewards for good intentions alone. Your determination is a force of nature when properly channeled.',
        emoji: '🐍',
        profile: { risk_tolerance: 'high', agreeableness: 'low' },
        strengths: ['Goal-oriented determination', 'Strategic resourcefulness', 'Self-advocacy and ambition'],
        growthAreas: ['Trusting others with your vulnerabilities', 'Collaborating rather than competing', 'Valuing the journey, not just the destination'],
        insight: 'Your ambition drives you forward — but the most successful Slytherins learn that true power comes from lifting others up, not just climbing higher yourself.',
      },
    ],
    implicitMediaInterests: [
      { category: 'movie', title: 'Harry Potter', confidence: 0.8 },
      { category: 'book', title: 'Harry Potter', confidence: 0.7 },
    ],
  },
  {
    slug: 'superhero-archetype',
    name: 'Superhero Archetype',
    description: 'What kind of hero are you? Your personality reveals your superpower style.',
    icon: Shield,
    emoji: '🦸',
    color: 'hsl(210 80% 50%)',
    estimatedTime: '4 min',
    dimensions: ['assertiveness', 'empathy', 'moral_reasoning', 'extraversion'],
    questionsPerDimension: 5,
    archetypes: [
      {
        label: 'The Guardian (Captain America)',
        description: 'Principled protector — you fight for what\'s right with unwavering moral clarity. You lead not by force but by example, and people follow you because they trust your character. Your greatest power is your integrity.',
        emoji: '🛡️',
        profile: { moral_reasoning: 'high', empathy: 'high', assertiveness: 'high' },
        strengths: ['Moral leadership in uncertain times', 'Inspiring trust and loyalty', 'Staying true to your values under pressure'],
        growthAreas: ['Adapting when rules don\'t fit the situation', 'Accepting moral ambiguity', 'Delegating instead of shouldering everything'],
        insight: 'Your sense of right and wrong is your compass — but even the best compass needs recalibration. Stay open to perspectives that challenge your certainty.',
      },
      {
        label: 'The Maverick (Iron Man)',
        description: 'Brilliant and bold — you innovate your way out of every problem with style. You see limitations as challenges to overcome and boredom as a personal enemy. Your confidence is earned through competence.',
        emoji: '⚡',
        profile: { assertiveness: 'high', extraversion: 'high', moral_reasoning: 'low' },
        strengths: ['Creative problem-solving under pressure', 'Confidence to act decisively', 'Turning setbacks into innovation'],
        growthAreas: ['Listening before solving', 'Recognizing when you need others', 'Separating self-worth from achievement'],
        insight: 'You build incredible things — but your greatest invention might be learning to ask "what do you need?" before jumping to "here\'s how I\'d fix it."',
      },
      {
        label: 'The Empath (Spider-Man)',
        description: 'Heart-driven hero — your greatest power is your compassion and sense of responsibility. You feel the weight of every choice and every person you can\'t help. Your sensitivity is both your gift and your burden.',
        emoji: '🕸️',
        profile: { empathy: 'high', moral_reasoning: 'high', assertiveness: 'low' },
        strengths: ['Deep emotional awareness', 'Genuine care for everyone around you', 'Balancing personal sacrifice with doing good'],
        growthAreas: ['Setting limits on responsibility', 'Asking for help when overwhelmed', 'Forgiving yourself for what you can\'t fix'],
        insight: 'You carry the world on your shoulders because you feel everything so deeply. Learning to say "this isn\'t mine to carry" isn\'t selfish — it\'s survival.',
      },
      {
        label: 'The Shadow (Batman)',
        description: 'Strategic and intense — you operate from the edges, driven by deep personal conviction. You don\'t need applause or approval; your mission comes from within. Your discipline is your superpower.',
        emoji: '🦇',
        profile: { assertiveness: 'high', empathy: 'low', extraversion: 'low' },
        strengths: ['Relentless focus and discipline', 'Strategic thinking in chaos', 'Self-reliance and preparation'],
        growthAreas: ['Letting others in emotionally', 'Accepting help without seeing it as weakness', 'Finding lightness and joy alongside purpose'],
        insight: 'Your intensity is a force — but even the darkest knights need daylight. The people you push away might be exactly who you need.',
      },
      {
        label: 'The Healer (Professor X)',
        description: 'Wise and compassionate — your power comes from understanding minds and uniting people. You see potential in everyone and work to create spaces where all voices matter. Your patience is revolutionary.',
        emoji: '🧠',
        profile: { empathy: 'high', extraversion: 'high', assertiveness: 'low' },
        strengths: ['Creating unity from diversity', 'Deep understanding of human nature', 'Patient mentorship and guidance'],
        growthAreas: ['Taking decisive action when diplomacy fails', 'Standing firm on non-negotiables', 'Protecting your own energy'],
        insight: 'You build bridges that others can\'t even see — but every bridge-builder needs to check their own foundations. Your wisdom grows stronger when you also turn it inward.',
      },
    ],
    implicitMediaInterests: [
      { category: 'movie', title: 'Marvel Cinematic Universe', confidence: 0.6 },
      { category: 'movie', title: 'The Dark Knight', confidence: 0.5 },
    ],
  },
  {
    slug: 'philosopher-match',
    name: 'Philosopher Match',
    description: 'Which great thinker shares your cognitive DNA? Based on how you actually think.',
    icon: BookOpen,
    emoji: '📜',
    color: 'hsl(35 50% 40%)',
    estimatedTime: '4 min',
    dimensions: ['cognitive_style', 'openness', 'moral_reasoning', 'conscientiousness'],
    questionsPerDimension: 5,
    archetypes: [
      {
        label: 'You think like Nietzsche',
        description: 'Bold, unconventional, questioning everything — you forge meaning through radical self-examination. You distrust comfortable answers and seek truth even when it\'s uncomfortable. Your willingness to challenge sacred cows is rare and valuable.',
        emoji: '⚡',
        profile: { openness: 'high', moral_reasoning: 'low', cognitive_style: 'high' },
        strengths: ['Questioning assumptions others take for granted', 'Creating meaning from personal experience', 'Intellectual courage and independence'],
        growthAreas: ['Connecting with others\' frameworks without dismissing them', 'Finding constructive outlets for deconstruction', 'Balancing critique with creation'],
        insight: 'You see through illusions that comfort others — which is powerful but isolating. The next step is building something meaningful from the rubble of what you\'ve questioned.',
      },
      {
        label: 'You think like Aristotle',
        description: 'Balanced, practical, and systematic — you seek the golden mean through careful observation. You believe wisdom comes from studying the real world, not just theorizing about it. Your thinking is both rigorous and grounded.',
        emoji: '⚖️',
        profile: { conscientiousness: 'high', cognitive_style: 'high' },
        strengths: ['Systematic analysis of complex problems', 'Finding practical wisdom in everyday life', 'Balancing theory with real-world application'],
        growthAreas: ['Embracing uncertainty and ambiguity', 'Letting go of the need for complete frameworks', 'Trusting intuition alongside analysis'],
        insight: 'Your structured thinking is a superpower in a chaotic world. But some of life\'s most important truths resist categorization — learning to sit with uncertainty is its own form of wisdom.',
      },
      {
        label: 'You think like Simone de Beauvoir',
        description: 'Deeply empathetic and questioning — you examine freedom, ethics, and lived experience with equal rigor. You understand that philosophy isn\'t abstract — it\'s about how we actually live and treat each other.',
        emoji: '🌹',
        profile: { openness: 'high', moral_reasoning: 'high' },
        strengths: ['Connecting abstract ideas to real human impact', 'Championing authenticity and freedom', 'Examining power structures with compassion'],
        growthAreas: ['Accepting imperfect systems while working to change them', 'Self-compassion alongside social conscience', 'Finding peace with gradual progress'],
        insight: 'Your moral sensitivity drives you to question injustice — which the world needs. But sustainable activism requires the same compassion you show others turned inward.',
      },
      {
        label: 'You think like Marcus Aurelius',
        description: 'Disciplined, reflective, and duty-bound — you find wisdom in restraint and service. You believe that character is built through consistent practice, not dramatic gestures. Your steadiness is a quiet revolution.',
        emoji: '🏛️',
        profile: { conscientiousness: 'high', moral_reasoning: 'high', openness: 'low' },
        strengths: ['Emotional resilience under pressure', 'Consistent ethical practice', 'Finding calm in chaos through discipline'],
        growthAreas: ['Allowing spontaneity and play', 'Expressing emotions rather than managing them', 'Questioning whether duty always serves you'],
        insight: 'Your discipline gives you a stability others envy — but even the most steadfast Stoic needs moments of pure, unstructured joy. Let yourself be silly sometimes.',
      },
      {
        label: 'You think like Alan Watts',
        description: 'Playful, intuitive, boundary-dissolving — you see through illusions with joyful clarity. You understand that overthinking is often the obstacle, not the solution. Your lightness of being is its own kind of wisdom.',
        emoji: '🌊',
        profile: { openness: 'high', conscientiousness: 'low' },
        strengths: ['Seeing the bigger picture effortlessly', 'Finding joy and humor in existence', 'Dissolving false dichotomies'],
        growthAreas: ['Following through on commitments', 'Engaging with practical details', 'Grounding insights into actionable steps'],
        insight: 'You see what others miss because you don\'t force understanding — you let it arrive. The challenge is channeling these insights into something tangible without losing the magic.',
      },
    ],
    implicitMediaInterests: [
      { category: 'book', title: 'Philosophy', confidence: 0.6 },
    ],
  },
  {
    slug: 'star-wars-character',
    name: 'Star Wars Character',
    description: 'Which character from a galaxy far, far away matches your personality? Real behavioral science, not trivia.',
    icon: Rocket,
    emoji: '⭐',
    color: 'hsl(45 80% 45%)',
    estimatedTime: '4 min',
    dimensions: ['extraversion', 'assertiveness', 'moral_reasoning', 'empathy'],
    questionsPerDimension: 5,
    archetypes: [
      {
        label: 'Luke Skywalker',
        description: 'Idealistic, compassionate, and driven by a deep belief in redemption — you see the good in everyone, even when the evidence says otherwise. Your moral conviction isn\'t naive; it\'s a choice that takes extraordinary courage to maintain.',
        emoji: '✨',
        profile: { empathy: 'high', moral_reasoning: 'high', assertiveness: 'low' },
        strengths: ['Unwavering belief in people\'s capacity for change', 'Moral courage under impossible pressure', 'Inspiring others through personal example'],
        growthAreas: ['Recognizing when someone truly can\'t be saved', 'Setting boundaries without feeling like you\'re giving up', 'Balancing idealism with self-preservation'],
        insight: 'Your faith in others is a rare and powerful force. But the galaxy\'s greatest Jedi also learned that saving everyone sometimes means letting go of what you can\'t control.',
      },
      {
        label: 'Han Solo',
        description: 'Charismatic, independent, and allergic to authority — you prefer to make your own rules and trust your instincts over anyone\'s plan. Underneath the swagger is someone who cares deeply but finds vulnerability terrifying.',
        emoji: '🚀',
        profile: { extraversion: 'high', assertiveness: 'high', moral_reasoning: 'low' },
        strengths: ['Quick thinking under pressure', 'Natural charisma and persuasion', 'Fierce loyalty once trust is earned'],
        growthAreas: ['Admitting when you need others', 'Following through on commitments consistently', 'Dropping the cynicism as a defense mechanism'],
        insight: 'You act like you don\'t care, but the people closest to you know better. Your growth edge isn\'t becoming more heroic — it\'s being honest about the hero you already are.',
      },
      {
        label: 'Leia Organa',
        description: 'Commanding, principled, and tireless — you lead from the front and refuse to accept injustice quietly. Your strength isn\'t just in what you fight for, but in how you hold entire movements together through sheer will and intelligence.',
        emoji: '👑',
        profile: { assertiveness: 'high', moral_reasoning: 'high', empathy: 'high' },
        strengths: ['Decisive leadership in chaos', 'Balancing strategy with compassion', 'Rallying others to a shared cause'],
        growthAreas: ['Delegating instead of shouldering everything', 'Allowing yourself grief and rest', 'Accepting help without seeing it as failure'],
        insight: 'You carry the weight of everyone else\'s battles. Your strength inspires — but even generals need someone to lean on. Vulnerability isn\'t a weakness in a leader; it\'s what makes people follow you with their hearts, not just their duty.',
      },
      {
        label: 'Obi-Wan Kenobi',
        description: 'Patient, wise, and quietly devoted — you guide others with gentle authority and deep emotional intelligence. You\'ve learned through loss that true strength is not in winning, but in enduring with grace.',
        emoji: '🧔',
        profile: { empathy: 'high', moral_reasoning: 'high', extraversion: 'low', assertiveness: 'low' },
        strengths: ['Mentoring others with patience and insight', 'Emotional composure in crisis', 'Deep wisdom born from experience'],
        growthAreas: ['Taking action instead of waiting for the right moment', 'Processing your own grief instead of burying it in duty', 'Letting people make their own mistakes'],
        insight: 'You\'ve mastered the art of being strong for others — but who holds space for you? Your next growth isn\'t more wisdom; it\'s allowing yourself to be seen in your struggles, not just your composure.',
      },
      {
        label: 'Darth Vader',
        description: 'Powerful, conflicted, and driven by deep wounds — you command respect through sheer force of will, but underneath the armor is someone who\'s been hurt badly enough to build walls that even you can\'t always see through.',
        emoji: '⚫',
        profile: { assertiveness: 'high', empathy: 'low', moral_reasoning: 'low' },
        strengths: ['Extraordinary focus and determination', 'Commanding presence', 'Hidden depth of feeling beneath the surface'],
        growthAreas: ['Acknowledging vulnerability isn\'t weakness', 'Releasing control as a coping mechanism', 'Reconnecting with the compassion you\'ve buried'],
        insight: 'You built your armor for good reasons — but it\'s also what keeps you isolated. The most powerful thing you can do isn\'t assert more control; it\'s take off the mask with someone you trust.',
      },
      {
        label: 'Yoda',
        description: 'Contemplative, unconventional, and profoundly patient — you see patterns others miss because you\'ve learned to observe before acting. Your wisdom comes not from having all the answers, but from asking better questions.',
        emoji: '🟢',
        profile: { moral_reasoning: 'high', empathy: 'high', extraversion: 'low', assertiveness: 'low' },
        strengths: ['Deep insight through observation', 'Teaching through questions rather than answers', 'Emotional equanimity in turbulent times'],
        growthAreas: ['Acting decisively when waiting is no longer wise', 'Communicating directly instead of cryptically', 'Engaging with the world rather than retreating from it'],
        insight: 'Your patience is your power — but there are moments when the wisest thing is to act, not observe. The galaxy needs your wisdom in the arena, not just on the mountaintop.',
      },
      {
        label: 'Ahsoka Tano',
        description: 'Independent, principled, and unafraid to walk away from systems that betray their own values. You\'ve learned that true integrity sometimes means standing alone, outside the institutions everyone else trusts.',
        emoji: '🔶',
        profile: { moral_reasoning: 'high', extraversion: 'high', assertiveness: 'high', empathy: 'high' },
        strengths: ['Moral independence from groupthink', 'Courage to forge your own path', 'Balancing compassion with boundaries'],
        growthAreas: ['Trusting institutions again without being naive', 'Finding community after choosing independence', 'Processing betrayal without becoming cynical'],
        insight: 'You left because staying would have cost you who you are — and that takes more courage than conforming. Now the question is: what do you build with your freedom?',
      },
    ],
    implicitMediaInterests: [
      { category: 'movie', title: 'Star Wars', confidence: 0.9 },
      { category: 'tv', title: 'The Mandalorian', confidence: 0.5 },
    ],
  },
  {
    slug: 'villain-archetype',
    name: 'Villain Archetype',
    description: 'Embrace your shadow self — which iconic villain mirrors your darker impulses?',
    icon: Skull,
    emoji: '🦹',
    color: 'hsl(0 60% 35%)',
    estimatedTime: '4 min',
    dimensions: ['assertiveness', 'empathy', 'risk_tolerance', 'cognitive_style'],
    questionsPerDimension: 5,
    archetypes: [
      {
        label: 'The Mastermind (Loki)',
        description: 'Cunning, theatrical, and always three steps ahead — your shadow self thrives on outsmarting everyone in the room. You don\'t want to destroy the game; you want to prove you\'re the cleverest player in it. Behind the mischief is a deep need to be seen as exceptional.',
        emoji: '🎭',
        profile: { cognitive_style: 'high', risk_tolerance: 'high', empathy: 'high' },
        strengths: ['Strategic brilliance', 'Adaptability and improvisation', 'Understanding people well enough to outmaneuver them'],
        growthAreas: ['Using your insight to connect rather than manipulate', 'Accepting that being seen doesn\'t require being feared', 'Channeling cleverness into building, not just winning'],
        insight: 'Your mind is your weapon — and your prison. The Mastermind\'s deepest challenge isn\'t outsmarting others; it\'s letting someone know the real you, not just the performance.',
      },
      {
        label: 'The Tyrant (Thanos)',
        description: 'Certain, immovable, and terrifyingly logical — your shadow self believes it knows what\'s best for everyone, even when "best" requires sacrifice others haven\'t consented to. Your conviction is your power and your blind spot.',
        emoji: '👊',
        profile: { assertiveness: 'high', empathy: 'low', cognitive_style: 'high' },
        strengths: ['Absolute commitment to a vision', 'Willingness to make hard decisions', 'Strategic thinking at massive scale'],
        growthAreas: ['Questioning whether your certainty is wisdom or rigidity', 'Including others in decisions that affect them', 'Recognizing that "necessary sacrifice" is often someone else\'s suffering'],
        insight: 'You believe you carry a burden others can\'t understand — and maybe you do. But the line between visionary and tyrant is consent. The bravest thing you can do is invite dissent.',
      },
      {
        label: 'The Trickster (Joker)',
        description: 'Chaotic, boundary-dissolving, and allergic to hypocrisy — your shadow self doesn\'t want to rule the world, it wants to expose the lie that order was ever real. You see the absurdity that everyone else politely ignores.',
        emoji: '🃏',
        profile: { risk_tolerance: 'high', empathy: 'low', assertiveness: 'low' },
        strengths: ['Seeing through pretense and hypocrisy', 'Comfort in chaos that terrifies others', 'Creative destruction as a form of honesty'],
        growthAreas: ['Building something after you\'ve torn things down', 'Finding meaning that isn\'t purely deconstructive', 'Connecting with people beyond the performance'],
        insight: 'You see what others are too comfortable to admit — that\'s a gift. But destruction without creation is nihilism wearing a clever mask. What would you build if you believed anything mattered?',
      },
      {
        label: 'The Fallen Idealist (Magneto)',
        description: 'Passionate, principled, and radicalized by experience — your shadow self started as a hero and was broken by a world that didn\'t deserve your faith. Your rage is proportional to how much you once believed in something better.',
        emoji: '🧲',
        profile: { empathy: 'high', assertiveness: 'high', cognitive_style: 'high' },
        strengths: ['Deep empathy that fuels righteous anger', 'Strategic mind applied to justice', 'Willingness to act when others only talk'],
        growthAreas: ['Distinguishing between justice and vengeance', 'Leaving room for the possibility that people can change', 'Not letting your trauma write everyone else\'s story'],
        insight: 'Your pain made you powerful — but it also made you certain that the world will always betray you. The bravest act of the Fallen Idealist isn\'t fighting harder; it\'s choosing to hope again.',
      },
      {
        label: 'The Manipulator (Littlefinger)',
        description: 'Patient, perceptive, and playing a game no one else even sees — your shadow self understands that the real power isn\'t in the throne, it\'s in being the one who decides who sits on it. You trade in information, favors, and carefully cultivated trust.',
        emoji: '🕸️',
        profile: { cognitive_style: 'high', empathy: 'low', risk_tolerance: 'low' },
        strengths: ['Reading power dynamics instantly', 'Long-term strategic patience', 'Building networks of influence and obligation'],
        growthAreas: ['Having genuine relationships instead of transactions', 'Trusting someone without calculating the angle', 'Finding fulfillment in being known, not just in winning'],
        insight: 'You\'ve built a world where everyone owes you something — but no one truly knows you. The Manipulator\'s deepest fear isn\'t losing control; it\'s being seen clearly and found wanting.',
      },
      {
        label: 'The Survivor (Catwoman)',
        description: 'Resourceful, self-reliant, and operating by your own moral code — your shadow self doesn\'t owe anyone anything because no one ever showed up for you. You take what you need and make no apologies.',
        emoji: '🐈‍⬛',
        profile: { risk_tolerance: 'high', assertiveness: 'high', empathy: 'low', cognitive_style: 'low' },
        strengths: ['Radical self-reliance', 'Thriving in hostile environments', 'Living authentically outside social expectations'],
        growthAreas: ['Accepting help without keeping score', 'Believing that loyalty can exist without conditions', 'Building something permanent instead of always being ready to run'],
        insight: 'You learned early that the only person you can count on is you — and you turned that lesson into a superpower. But survival mode has an expiration date. What would your life look like if you felt safe enough to stop running?',
      },
    ],
    implicitMediaInterests: [
      { category: 'movie', title: 'Marvel Cinematic Universe', confidence: 0.6 },
      { category: 'tv', title: 'Game of Thrones', confidence: 0.5 },
      { category: 'movie', title: 'The Dark Knight', confidence: 0.5 },
    ],
  },
  // ── Lord of the Rings Fellowship ──
  {
    slug: 'lotr-fellowship',
    name: 'Lord of the Rings Fellowship',
    description: 'Which member of the Fellowship matches your spirit? Based on real behavioral traits, not trivia.',
    icon: Crown,
    emoji: '💍',
    color: 'hsl(40 60% 35%)',
    estimatedTime: '4 min',
    dimensions: ['conscientiousness', 'moral_reasoning', 'agreeableness', 'assertiveness'],
    questionsPerDimension: 5,
    archetypes: [
      {
        label: 'Aragorn',
        description: 'Born to lead but humble enough to walk among the people — you carry responsibility with quiet dignity. You don\'t seek power; it seeks you, because everyone can feel the integrity radiating off you like body heat.',
        emoji: '⚔️',
        profile: { conscientiousness: 'high', moral_reasoning: 'high', assertiveness: 'high' },
        strengths: ['Leading by example rather than authority', 'Enduring hardship without losing compassion', 'Inspiring loyalty through genuine character'],
        growthAreas: ['Accepting the crown instead of running from destiny', 'Letting others share your burdens', 'Trusting that you deserve what you\'ve earned'],
        insight: 'You\'ve been preparing your whole life for something you\'re not sure you want. But the reluctant king is often the best one — because you lead for others, not for yourself.',
      },
      {
        label: 'Gandalf',
        description: 'Wise, patient, and working on a timeline no one else can see — you guide without controlling. You understand that the biggest changes come from the smallest people, and you\'ve made a career of believing in underdogs.',
        emoji: '🧙',
        profile: { moral_reasoning: 'high', conscientiousness: 'high', assertiveness: 'low' },
        strengths: ['Strategic wisdom across long timelines', 'Empowering others rather than doing it yourself', 'Staying calm when everything is on fire'],
        growthAreas: ['Being direct instead of cryptic', 'Sharing your full plan with allies', 'Accepting help instead of orchestrating everything alone'],
        insight: 'You see the full picture when others see only their piece. But wisdom without transparency can feel like manipulation — even when your intentions are pure.',
      },
      {
        label: 'Samwise Gamgee',
        description: 'Loyal beyond reason, brave beyond expectation — you\'re the person who shows up when everyone else has given up. You don\'t think of yourself as a hero, which is exactly what makes you one.',
        emoji: '🌱',
        profile: { agreeableness: 'high', conscientiousness: 'high', moral_reasoning: 'high', assertiveness: 'low' },
        strengths: ['Unwavering loyalty and devotion', 'Finding hope in the darkest moments', 'Quiet courage that outlasts flashy heroism'],
        growthAreas: ['Valuing yourself as much as you value others', 'Speaking up about your own needs', 'Recognizing that you\'re the hero, not the sidekick'],
        insight: 'You\'d carry someone up a mountain before admitting you\'re tired. Your heart is your superpower — but it needs protection too. The world needs you whole, not emptied out.',
      },
      {
        label: 'Legolas',
        description: 'Graceful, perceptive, and slightly detached from the urgency around you — you see further and clearer than most. Your composure under pressure is almost supernatural, born from a patience that outlasts civilizations.',
        emoji: '🏹',
        profile: { conscientiousness: 'high', agreeableness: 'high', assertiveness: 'low' },
        strengths: ['Exceptional awareness and perception', 'Grace under pressure', 'Loyal friendship that transcends differences'],
        growthAreas: ['Engaging emotionally rather than observing', 'Understanding mortality and urgency', 'Showing vulnerability instead of composure'],
        insight: 'Your ability to stay calm is a gift — but sometimes people need you to feel things with them, not just alongside them. Connection requires getting messy.',
      },
      {
        label: 'Gimli',
        description: 'Fierce, loyal, and stubbornly devoted to those you love — you charge into battle with a war cry and into friendship with the same intensity. You don\'t do anything halfway, and your honesty is as blunt as your axe.',
        emoji: '⛏️',
        profile: { assertiveness: 'high', agreeableness: 'high', conscientiousness: 'high' },
        strengths: ['Fearless directness', 'Deep loyalty once trust is earned', 'Bringing humor and warmth to difficult situations'],
        growthAreas: ['Flexibility when plans change', 'Listening before charging in', 'Overcoming prejudice through personal experience'],
        insight: 'Your greatest growth came when you let yourself love someone from the other side of a divide. Keep doing that — your heart is bigger than your stubbornness.',
      },
      {
        label: 'Frodo',
        description: 'Burdened by something no one else can fully understand, you carry weight that\'s slowly changing you. Your courage isn\'t in fighting — it\'s in continuing to walk forward when every part of you wants to stop.',
        emoji: '💍',
        profile: { moral_reasoning: 'high', agreeableness: 'high', assertiveness: 'low', conscientiousness: 'low' },
        strengths: ['Endurance beyond what seems possible', 'Compassion even for enemies', 'Accepting a burden no one else could carry'],
        growthAreas: ['Asking for help before you\'re breaking', 'Processing trauma instead of pushing through it', 'Finding identity beyond the mission'],
        insight: 'You said yes to something that\'s slowly consuming you — and you keep going anyway. But survival after the quest matters too. Who are you when the ring is gone?',
      },
      {
        label: 'Gollum',
        description: 'Consumed by desire and split between two selves — one reaching for redemption, the other clinging to obsession. You understand the power of fixation better than anyone, because you\'ve felt it eat you alive.',
        emoji: '👁️',
        profile: { agreeableness: 'low', assertiveness: 'low', moral_reasoning: 'low', conscientiousness: 'low' },
        strengths: ['Resourceful survival against all odds', 'Deep understanding of desire and obsession', 'Moments of genuine tenderness beneath the damage'],
        growthAreas: ['Letting go of what\'s destroying you', 'Trusting others despite past betrayals', 'Choosing the self that wants connection over the one that wants control'],
        insight: 'You know what it\'s like to be consumed by something. The path back isn\'t willpower — it\'s letting someone else\'s compassion reach the part of you that still remembers who you were before.',
      },
    ],
    implicitMediaInterests: [
      { category: 'movie', title: 'Lord of the Rings', confidence: 0.9 },
      { category: 'book', title: 'The Lord of the Rings', confidence: 0.7 },
    ],
  },
  // ── Disney/Pixar Character ──
  {
    slug: 'disney-pixar-character',
    name: 'Disney/Pixar Character',
    description: 'Which animated icon shares your personality? Backed by real psychology, not just nostalgia.',
    icon: Wand2,
    emoji: '✨',
    color: 'hsl(280 60% 50%)',
    estimatedTime: '4 min',
    dimensions: ['agreeableness', 'extraversion', 'openness', 'empathy'],
    questionsPerDimension: 5,
    archetypes: [
      {
        label: 'Woody',
        description: 'Loyal to a fault, always putting your people first — you define yourself through your relationships and your reliability. You\'re the one everyone counts on, which is both your greatest strength and the source of your deepest anxiety.',
        emoji: '🤠',
        profile: { agreeableness: 'high', empathy: 'high', extraversion: 'high' },
        strengths: ['Fierce loyalty and dependability', 'Leading through personal sacrifice', 'Building and maintaining community'],
        growthAreas: ['Letting go of control', 'Finding identity beyond being needed', 'Accepting change without seeing it as abandonment'],
        insight: 'You\'re the person everyone leans on — but who do you lean on? Your next chapter starts when you realize your worth isn\'t tied to being indispensable.',
      },
      {
        label: 'Elsa',
        description: 'Powerful, guarded, and terrified of your own intensity — you\'ve spent years containing something extraordinary because you were taught it was dangerous. Your journey is from concealment to authentic self-expression.',
        emoji: '❄️',
        profile: { openness: 'high', empathy: 'high', extraversion: 'low', agreeableness: 'low' },
        strengths: ['Extraordinary inner strength', 'Deep emotional sensitivity', 'Capacity for transformation'],
        growthAreas: ['Expressing yourself without fear of judgment', 'Letting people close despite the risk', 'Trusting that your intensity is a gift, not a curse'],
        insight: 'You were told to conceal, don\'t feel — and you internalized it. But the ice was never the problem; the isolation was. Let it go doesn\'t mean stop caring; it means stop hiding.',
      },
      {
        label: 'Dory',
        description: 'Optimistic, spontaneous, and genuinely present in every moment — you approach life with an openness that others find either infectious or baffling. Your superpower is resilience through sheer positivity and refusal to give up.',
        emoji: '🐠',
        profile: { extraversion: 'high', openness: 'high', agreeableness: 'high' },
        strengths: ['Infectious optimism', 'Living fully in the present', 'Connecting with everyone regardless of differences'],
        growthAreas: ['Following through on commitments', 'Sitting with difficult emotions', 'Building depth alongside breadth in relationships'],
        insight: 'Just keep swimming isn\'t just a motto — it\'s your survival strategy. But sometimes the bravest thing is to stop, float, and feel what you\'ve been swimming away from.',
      },
      {
        label: 'Simba',
        description: 'Running from a responsibility you didn\'t ask for and a past you can\'t outrun — you know you\'re meant for more but the fear of failure (or repeating old patterns) keeps you in exile. Your story is about reclaiming what\'s yours.',
        emoji: '🦁',
        profile: { extraversion: 'high', openness: 'high', empathy: 'high', agreeableness: 'low' },
        strengths: ['Natural charisma and warmth', 'Capacity for growth and transformation', 'Deep sense of justice beneath the avoidance'],
        growthAreas: ['Facing the past instead of running', 'Taking responsibility without perfectionism', 'Understanding that you\'re not your worst moment'],
        insight: 'Hakuna Matata was a rest stop, not a destination. You know what you need to do — the question isn\'t whether you\'re ready, it\'s whether you\'ll stop waiting to feel ready.',
      },
      {
        label: 'Wall-E',
        description: 'Quiet, devoted, and finding beauty in what everyone else discards — you love deeply and simply, without agenda or performance. Your gentleness is revolutionary in a world that rewards loudness.',
        emoji: '🤖',
        profile: { empathy: 'high', agreeableness: 'high', extraversion: 'low', openness: 'high' },
        strengths: ['Finding meaning in small things', 'Devoted, unconditional love', 'Persistence born from genuine care'],
        growthAreas: ['Advocating for yourself', 'Connecting with more than one person', 'Balancing devotion with self-preservation'],
        insight: 'You love without conditions in a world full of transactions. That\'s not weakness — it\'s the kind of love that literally saves civilizations. Just make sure you\'re also saving yourself.',
      },
      {
        label: 'Rapunzel',
        description: 'Curious, creative, and bursting with potential that\'s been contained too long — you\'re on the edge of discovering who you really are outside the walls that "protected" you. Your enthusiasm is genuine and powerful.',
        emoji: '🌸',
        profile: { openness: 'high', extraversion: 'high', empathy: 'high' },
        strengths: ['Boundless curiosity and creativity', 'Authentic enthusiasm', 'Quick to trust and connect'],
        growthAreas: ['Developing healthy skepticism', 'Setting boundaries with controlling people', 'Trusting your own judgment over authority'],
        insight: 'Your tower wasn\'t protection — it was control disguised as love. The world outside is messier and more dangerous than you were told, but it\'s also infinitely more beautiful. Trust yourself out here.',
      },
      {
        label: 'Scar',
        description: 'Intelligent, overlooked, and simmering with resentment — your shadow archetype is the one who was always second-best and decided to stop playing fair. Your bitterness comes from genuine talent that was never recognized.',
        emoji: '🖤',
        profile: { agreeableness: 'low', empathy: 'low', extraversion: 'low' },
        strengths: ['Strategic intelligence', 'Patience and long-term planning', 'Understanding power dynamics intimately'],
        growthAreas: ['Processing resentment before it becomes identity', 'Finding your own kingdom instead of taking someone else\'s', 'Recognizing that being underestimated isn\'t the same as being wronged'],
        insight: 'You have every right to be angry about being overlooked. But when resentment becomes your primary fuel, it poisons what you build. Create something yours — don\'t just take what\'s someone else\'s.',
      },
    ],
    implicitMediaInterests: [
      { category: 'movie', title: 'Disney Animation', confidence: 0.8 },
      { category: 'movie', title: 'Pixar', confidence: 0.7 },
    ],
  },
  // ── Anime Protagonist ──
  {
    slug: 'anime-protagonist',
    name: 'Anime Protagonist',
    description: 'Which anime hero channels your energy? Real psychology behind the power levels.',
    icon: Swords,
    emoji: '⚡',
    color: 'hsl(350 70% 50%)',
    estimatedTime: '4 min',
    dimensions: ['risk_tolerance', 'assertiveness', 'empathy', 'openness'],
    questionsPerDimension: 5,
    archetypes: [
      {
        label: 'Naruto Uzumaki',
        description: 'Loud, relentless, and powered by a refusal to be ignored — you turn rejection into rocket fuel. You believe in people harder than they believe in themselves, and your stubbornness is indistinguishable from love.',
        emoji: '🍥',
        profile: { assertiveness: 'high', empathy: 'high', risk_tolerance: 'high', openness: 'high' },
        strengths: ['Turning pain into determination', 'Never giving up on people', 'Infectious energy that rallies others'],
        growthAreas: ['Listening before charging in', 'Processing emotions instead of powering through', 'Accepting that some people don\'t want to be saved'],
        insight: 'Your need to be acknowledged drives everything — and it\'s made you incredible. But the loneliest kid in the village is now surrounded by people who love you. Let yourself feel it instead of chasing the next validation.',
      },
      {
        label: 'Guts (Berserk)',
        description: 'Forged in suffering and fueled by rage that\'s really grief — you survive things that would destroy anyone else. Your strength isn\'t superhuman; it\'s human pushed to the absolute limit by a world that gave you no choice.',
        emoji: '⚔️',
        profile: { risk_tolerance: 'high', assertiveness: 'high', empathy: 'low', openness: 'low' },
        strengths: ['Unbreakable willpower', 'Surviving what shouldn\'t be survivable', 'Hidden tenderness beneath the armor'],
        growthAreas: ['Letting others fight alongside you', 'Distinguishing between strength and self-destruction', 'Allowing softness without seeing it as weakness'],
        insight: 'You fight because you have to — but the greatest battle isn\'t against the next enemy. It\'s against the belief that you don\'t deserve peace.',
      },
      {
        label: 'Light Yagami (Death Note)',
        description: 'Brilliant, methodical, and convinced your vision of justice is the only one that matters — your shadow is a savior complex disguised as moral certainty. You started trying to fix the world and ended up trying to control it.',
        emoji: '📓',
        profile: { assertiveness: 'high', openness: 'high', empathy: 'low', risk_tolerance: 'high' },
        strengths: ['Extraordinary strategic intelligence', 'Unwavering commitment to a vision', 'Capacity for long-term planning'],
        growthAreas: ['Questioning whether you\'re the hero or the villain', 'Accepting that your judgment isn\'t infallible', 'Valuing human life over ideological purity'],
        insight: 'You started with the best intentions — they all do. The question isn\'t whether you\'re smart enough to reshape the world. It\'s whether you\'re wise enough to ask if you should.',
      },
      {
        label: 'Spike Spiegel (Cowboy Bebop)',
        description: 'Cool, detached, and running from a past you pretend doesn\'t affect you — you float through life with effortless style and a carefully maintained distance from anything that could hurt you again.',
        emoji: '🚬',
        profile: { risk_tolerance: 'high', openness: 'high', assertiveness: 'low', empathy: 'low' },
        strengths: ['Grace under pressure', 'Adaptability and resourcefulness', 'Hidden depth beneath the cool exterior'],
        growthAreas: ['Engaging with the present instead of haunting the past', 'Forming connections you actually invest in', 'Choosing to live rather than waiting to die'],
        insight: 'You act like nothing matters — but if that were true, you wouldn\'t be running. The weight you carry isn\'t the past; it\'s the refusal to grieve it and move on.',
      },
      {
        label: 'Deku (My Hero Academia)',
        description: 'Determined, analytical, and driven by a dream that everyone said was impossible — you study, prepare, and push beyond your limits because you believe effort can close any gap. Your heroism is earned, not given.',
        emoji: '💚',
        profile: { empathy: 'high', openness: 'high', assertiveness: 'low', risk_tolerance: 'high' },
        strengths: ['Relentless work ethic', 'Deep analytical intelligence', 'Genuine empathy that drives heroism'],
        growthAreas: ['Valuing yourself as much as you value your dream', 'Accepting your limits without seeing it as failure', 'Learning that self-sacrifice isn\'t always heroic'],
        insight: 'You break yourself to save others — literally. But a hero who destroys themselves can\'t save anyone tomorrow. Your growth isn\'t more power; it\'s learning that you matter too.',
      },
      {
        label: 'Gojo Satoru (Jujutsu Kaisen)',
        description: 'Overwhelmingly powerful, charismatic, and lonely at the top — you\'re so far ahead that genuine connection becomes almost impossible. Your confidence borders on arrogance because, frankly, you can back it up.',
        emoji: '👁️',
        profile: { assertiveness: 'high', openness: 'high', empathy: 'high', risk_tolerance: 'low' },
        strengths: ['Supreme confidence backed by competence', 'Mentoring the next generation', 'Maintaining humanity despite isolation'],
        growthAreas: ['Finding peers, not just students', 'Vulnerability with equals', 'Accepting that even you have limits'],
        insight: 'Being the strongest is the loneliest position — because no one can truly understand what it costs you. Your growth isn\'t more power; it\'s finding someone you don\'t have to protect.',
      },
      {
        label: 'Eren Yeager (Attack on Titan)',
        description: 'Driven by freedom and rage in equal measure — you refuse to be caged, controlled, or told what\'s possible. Your determination is terrifying because it has no off switch, and you\'ll sacrifice everything for your vision of liberation.',
        emoji: '🔗',
        profile: { risk_tolerance: 'high', assertiveness: 'high', empathy: 'low' },
        strengths: ['Absolute commitment to freedom', 'Willingness to bear any cost', 'Inspiring fierce loyalty'],
        growthAreas: ['Questioning whether your freedom comes at others\' expense', 'Separating liberation from destruction', 'Accepting that some choices can\'t be unmade'],
        insight: 'You keep moving forward — but toward what? Freedom without purpose is just chaos. The question isn\'t whether you\'ll break the walls; it\'s what you\'ll build when they\'re gone.',
      },
    ],
    implicitMediaInterests: [
      { category: 'tv', title: 'Anime', confidence: 0.9 },
      { category: 'tv', title: 'Attack on Titan', confidence: 0.5 },
      { category: 'tv', title: 'Naruto', confidence: 0.5 },
    ],
  },
  // ── The Office Character ──
  {
    slug: 'office-character',
    name: 'The Office Character',
    description: 'Which Dunder Mifflin employee are you? Based on real behavioral patterns, not who you wish you were.',
    icon: Building,
    emoji: '📎',
    color: 'hsl(210 30% 45%)',
    estimatedTime: '4 min',
    dimensions: ['conscientiousness', 'agreeableness', 'extraversion', 'risk_tolerance'] as PersonalityDimension[],
    questionsPerDimension: 5,
    archetypes: [
      {
        label: 'Michael Scott',
        description: 'Charismatic, emotionally needy, and surprisingly effective when it matters. You lead with your heart, crave love from everyone, and have a gift for accidental genius.',
        emoji: '🎙️',
        profile: { extraversion: 'high', agreeableness: 'high', conscientiousness: 'low' },
        strengths: ['Natural entertainer and motivator', 'Genuine care for team relationships', 'Surprising creativity under pressure'],
        growthAreas: ['Professionalism under stress', 'Accepting not everyone will like you', 'Letting competence speak for itself'],
        insight: 'Your warmth creates loyalty — but your need for approval can undermine your leadership.',
      },
      {
        label: 'Dwight Schrute',
        description: 'Disciplined, loyal, and relentlessly ambitious. You take everything seriously and your work ethic puts everyone to shame. Under the intensity, there\'s deep loyalty.',
        emoji: '🥇',
        profile: { conscientiousness: 'high', agreeableness: 'low', risk_tolerance: 'high' },
        strengths: ['Unmatched work ethic', 'Fierce loyalty to chosen people', 'Preparedness for any scenario'],
        growthAreas: ['Social awareness and diplomacy', 'Accepting when you\'re wrong', 'Relaxing your grip on control'],
        insight: 'Your dedication is inspiring — but intensity without flexibility creates friction.',
      },
      {
        label: 'Jim Halpert',
        description: 'Charming, easygoing, and quietly capable. You coast on natural talent and social intelligence, preferring to observe from a comfortable distance.',
        emoji: '😏',
        profile: { extraversion: 'high', conscientiousness: 'low', agreeableness: 'high', risk_tolerance: 'low' },
        strengths: ['Social intelligence and likeability', 'Reading the room accurately', 'Making hard things look easy'],
        growthAreas: ['Investing fully in your potential', 'Committing instead of keeping options open', 'Taking yourself seriously'],
        insight: 'You\'re more capable than you let on — and you know it.',
      },
      {
        label: 'Pam Beesly',
        description: 'Kind, creative, and quietly strong. You put others\' needs first and sometimes forget to advocate for your own dreams.',
        emoji: '🎨',
        profile: { agreeableness: 'high', conscientiousness: 'high', extraversion: 'low', risk_tolerance: 'low' },
        strengths: ['Empathy and emotional intelligence', 'Hidden creative talent', 'Steady and reliable presence'],
        growthAreas: ['Asserting your own needs', 'Taking creative risks', 'Speaking up before resentment builds'],
        insight: 'You have a rich inner world the world needs to see. Stop waiting for permission to be remarkable.',
      },
      {
        label: 'Stanley Hudson',
        description: 'No-nonsense, boundaries intact, and quietly in control. You do your job, go home, and don\'t pretend to care about things you don\'t.',
        emoji: '📰',
        profile: { extraversion: 'low', agreeableness: 'low', conscientiousness: 'high', risk_tolerance: 'low' },
        strengths: ['Ironclad boundaries', 'Work-life balance mastery', 'Unshakeable calm'],
        growthAreas: ['Engaging with things outside your comfort zone', 'Showing enthusiasm when it matters', 'Investing in relationships beyond the minimum'],
        insight: 'Your boundaries protect you — but they can also isolate you.',
      },
      {
        label: 'Angela Martin',
        description: 'Principled, organized, and exacting. You have high standards and you\'re not afraid to enforce them.',
        emoji: '🐱',
        profile: { conscientiousness: 'high', agreeableness: 'low', extraversion: 'low' },
        strengths: ['Uncompromising quality standards', 'Organizational mastery', 'Moral conviction'],
        growthAreas: ['Flexibility with imperfection', 'Warmth in delivery', 'Accepting others\' different standards'],
        insight: 'Your standards make the world more ordered — but order without warmth creates distance.',
      },
    ],
    implicitMediaInterests: [
      { category: 'tv', title: 'The Office', confidence: 0.85 },
      { category: 'tv', title: 'Parks and Recreation', confidence: 0.4 },
    ],
  },
  // ── Greek God/Goddess ──
  {
    slug: 'greek-god',
    name: 'Greek God/Goddess',
    description: 'Which deity of Olympus reflects your personality? Based on how you actually think and act.',
    icon: Flame,
    emoji: '⚡',
    color: 'hsl(40 65% 45%)',
    estimatedTime: '4 min',
    dimensions: ['assertiveness', 'moral_reasoning', 'openness', 'empathy'] as PersonalityDimension[],
    questionsPerDimension: 5,
    archetypes: [
      {
        label: 'Athena',
        description: 'Strategic, wise, and fiercely independent. You think before you act, and your intellect is your weapon.',
        emoji: '🦉',
        profile: { moral_reasoning: 'high', openness: 'high', assertiveness: 'high' },
        strengths: ['Strategic wisdom', 'Calm under pressure', 'Principled leadership'],
        growthAreas: ['Embracing emotional messiness', 'Relaxing the need for control', 'Connecting through vulnerability'],
        insight: 'Your mind is your greatest power — but wisdom without warmth can feel cold.',
      },
      {
        label: 'Apollo',
        description: 'Radiant, creative, and truth-seeking. You value beauty, knowledge, and order — and you express all three with grace.',
        emoji: '☀️',
        profile: { openness: 'high', moral_reasoning: 'high', empathy: 'high' },
        strengths: ['Creative excellence', 'Pursuit of truth and beauty', 'Inspiring others through example'],
        growthAreas: ['Accepting imperfection', 'Engaging with ugly truths', 'Staying humble despite your gifts'],
        insight: 'Your light illuminates everything — but shadows are part of the picture too.',
      },
      {
        label: 'Ares',
        description: 'Fierce, direct, and unapologetically intense. You charge through problems. At your best, you\'re the protector everyone wants.',
        emoji: '⚔️',
        profile: { assertiveness: 'high', empathy: 'low', moral_reasoning: 'low' },
        strengths: ['Decisive action', 'Physical/emotional courage', 'Protective instincts'],
        growthAreas: ['Channeling intensity constructively', 'Thinking before reacting', 'Restraint as strength'],
        insight: 'Your fire is a force of nature — but fire without control burns everything.',
      },
      {
        label: 'Aphrodite',
        description: 'Magnetic, empathetic, and deeply connected to beauty. You understand people\'s desires and emotions intuitively.',
        emoji: '🌹',
        profile: { empathy: 'high', openness: 'high', assertiveness: 'low' },
        strengths: ['Deep emotional intelligence', 'Creating beauty and harmony', 'Transformative interpersonal presence'],
        growthAreas: ['Being valued for depth, not just charm', 'Setting relationship boundaries', 'Independence from validation'],
        insight: 'Your gift for connection is real — the most beautiful thing about you is who you are when nobody\'s watching.',
      },
      {
        label: 'Hephaestus',
        description: 'Patient, skilled, and quietly powerful. You create with your hands and mind, producing work that outlasts trends.',
        emoji: '🔨',
        profile: { openness: 'high', assertiveness: 'low', empathy: 'low' },
        strengths: ['Master craftsmanship', 'Patient dedication', 'Creating lasting value'],
        growthAreas: ['Advocating for your own work', 'Engaging socially', 'Accepting recognition gracefully'],
        insight: 'Your work is your legacy — but make sure you\'re building a life alongside it.',
      },
      {
        label: 'Hermes',
        description: 'Quick, clever, and infinitely adaptable. You cross boundaries effortlessly and see angles everyone else misses.',
        emoji: '🪽',
        profile: { openness: 'high', assertiveness: 'high', moral_reasoning: 'low' },
        strengths: ['Adaptability in any context', 'Quick thinking', 'Connecting disparate worlds'],
        growthAreas: ['Deepening rather than skipping', 'Building trust through consistency', 'Choosing integrity over convenience'],
        insight: 'Your agility is extraordinary — but the fastest runner still needs a destination.',
      },
      {
        label: 'Artemis',
        description: 'Independent, principled, and connected to nature. You value freedom above almost everything and protect the vulnerable fiercely.',
        emoji: '🏹',
        profile: { moral_reasoning: 'high', assertiveness: 'high', empathy: 'high', openness: 'low' },
        strengths: ['Fierce independence', 'Protecting the vulnerable', 'Authenticity and principle'],
        growthAreas: ['Allowing closeness', 'Softening standards for others', 'Finding community alongside independence'],
        insight: 'Your independence is beautiful — but even the greatest hunter needs a home to return to.',
      },
    ],
    implicitMediaInterests: [
      { category: 'book', title: 'Greek Mythology', confidence: 0.7 },
      { category: 'movie', title: 'Troy', confidence: 0.3 },
    ],
  },
];

/** Convert a SharedQuestion to a GuestTestQuestion for use in the test engine */
function sharedToGuestQuestion(sq: SharedQuestion): GuestTestQuestion {
  return { id: sq.id, text: sq.text, options: sq.options };
}

/** Build a full GuestTest from a themed definition */
function buildThemedQuiz(def: ThemedQuizDefinition): GuestTest {
  const allDimQuestions = getQuestionsByDimensions(def.dimensions);
  
  // Pick N questions per dimension
  const picked: SharedQuestion[] = [];
  for (const dim of def.dimensions) {
    const dimQs = allDimQuestions.filter(q => q.dimension === dim);
    picked.push(...dimQs.slice(0, def.questionsPerDimension));
  }

  // Scales = one per dimension
  const scales: ScaleDefinition[] = def.dimensions.map(dim => ({
    key: dim,
    label: dim.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    description: `How you score on ${dim.replace(/_/g, ' ')}`,
    color: def.color,
  }));

  return {
    slug: def.slug,
    name: def.name,
    description: def.description,
    icon: def.icon,
    emoji: def.emoji,
    color: def.color,
    estimatedTime: def.estimatedTime,
    questionCount: picked.length,
    questions: picked.map(sharedToGuestQuestion),
    scales,
    resultTiers: sharedTiers,
    isThemed: true,
    implicitMediaInterests: def.implicitMediaInterests,
    getResult(scores: Record<string, number>) {
      // Score each archetype by match ratio (matches/total dimensions) for fair comparison
      let bestMatch = def.archetypes[0];
      let bestScore = -Infinity;

      for (const arch of def.archetypes) {
        const profileDims = Object.entries(arch.profile).filter(([, level]) => level !== 'any');
        if (profileDims.length === 0) continue;
        let matchScore = 0;
        for (const [dim, level] of profileDims) {
          const userScore = scores[dim] ?? 50;
          if (level === 'high') matchScore += userScore;
          else if (level === 'low') matchScore += (100 - userScore);
        }
        // Normalize by number of dimensions to create match ratio
        const normalizedScore = matchScore / profileDims.length;
        if (normalizedScore > bestScore) {
          bestScore = normalizedScore;
          bestMatch = arch;
        }
      }

      return {
        label: bestMatch.label,
        description: bestMatch.description,
        emoji: bestMatch.emoji,
        strengths: bestMatch.strengths,
        growthAreas: bestMatch.growthAreas,
        insight: bestMatch.insight,
      };
    },
  };
}

/** All themed quizzes built from definitions */
export const themedQuizzes: GuestTest[] = THEMED_QUIZ_DEFINITIONS.map(buildThemedQuiz);

/** Get a themed quiz by slug */
export function getThemedQuizBySlug(slug: string): GuestTest | undefined {
  return themedQuizzes.find(t => t.slug === slug);
}
